import React, { useCallback, useEffect, useState } from "react";
import { Button, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Wrapper from "~/shared/ui/Wrapper";
import { useGeoPlace } from "~/entity/Geo";
import type { IGeoPlaceItem } from "~/entity/Geo/model/shema";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";
import {
  AddressSelectionGuideFilter,
  useAddressSelectionGuideFilterValues,
} from "~/widgets/Filter";

import AddressSelectionGuideModal from "./modals/AddressSelectionGuideModal";
import {
  ADDRESS_SELECTION_GUIDE_SORTABLE_COLUMNS,
  getAddressSelectionGuideColumns,
} from "./model/tableAdapter";
import { ADDRESS_SELECTION_GUIDE_OPTIONS } from "./utils/options";

import s from "./s.less";

const AddressSelectionGuide = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const history = useHistory();
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setData = useSetLocationData();
  const setDataReplace = useSetLocationData(true);
  const {
    page,
    count,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();
  const [geoPlaces, setGeoPlaces] = useState<IGeoPlaceItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { getGeoPlaces } = useGeoPlace();
  const filterValues = useAddressSelectionGuideFilterValues();

  // Бэкенд GET /geo/places не принимает параметры сортировки,
  // поэтому сортировка клиентская, а в URL хранится только её состояние
  const { sortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: ADDRESS_SELECTION_GUIDE_SORTABLE_COLUMNS,
    setData,
  });

  const tableSortField =
    sortField && ADDRESS_SELECTION_GUIDE_SORTABLE_COLUMNS.includes(sortField)
      ? sortField
      : undefined;

  const getPlacesHandler = useCallback(async () => {
    setLoading(true);
    try {
      // Бэкенд ожидает 0-based страницы (PASDILER-2230); в URL тоже храним 0-based.
      const { data } = await getGeoPlaces({
        page: Math.max(0, Number(page) || 0),
        count: Number(count) || 25,
        name: filterValues.name,
        id: filterValues.id,
        parentId: filterValues.parentId,
        type: filterValues.type,
        socr: filterValues.socr,
        index: filterValues.index,
        cladrCode: filterValues.cladrCode,
        okatoCode: filterValues.okatoCode,
        fiasId: filterValues.fiasId,
        fiasValue: filterValues.fiasValue,
        active: filterValues.active,
      });
      setGeoPlaces(data?.items ?? []);
      setTotal(data?.total ?? 0);
    } catch (error) {
      console.error("Ошибка при получении справочника адресов:", error);
      setGeoPlaces([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    count,
    page,
    filterValues.id,
    filterValues.parentId,
    filterValues.name,
    filterValues.type,
    filterValues.socr,
    filterValues.index,
    filterValues.cladrCode,
    filterValues.okatoCode,
    filterValues.fiasId,
    filterValues.fiasValue,
    filterValues.active,
    getGeoPlaces,
  ]);

  useEffect(() => {
    getPlacesHandler();
  }, [getPlacesHandler]);

  const { handleCount, handlePage } = usePaginationParams();

  const goBack = () => history.replace("/editor/");

  const openCreateModal = () => setDataReplace("openModal", "Create");

  const handleAddressRowEdit = (record: { id: string | number }) => {
    setDataReplace("openModal", "Edit", "currentId", record.id);
  };

  const columns = getAddressSelectionGuideColumns(
    t,
    ADDRESS_SELECTION_GUIDE_OPTIONS,
    tableSortField,
    sortType,
  );

  return (
    <Wrapper.Content
      title={t("addressSelectionGuide.address_selection_title")}
      txtBtn={t("ui.back")}
      goBackHandler={goBack}
      filterComponent={<AddressSelectionGuideFilter />}
      controlComponent={
        hasPermission("addressselectionguide.create") ? (
          <Button
            onClick={openCreateModal}
            size={"middle"}
            icon={<PlusIcon size={16} />}
          >
            {t("addressSelectionGuide.add_address")}
          </Button>
        ) : undefined
      }
    >
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={geoPlaces}
        rowKey="id"
        loading={loading}
        onRowEdit={
          hasPermission("addressselectionguide.edit")
            ? handleAddressRowEdit
            : undefined
        }
        actionsFixed
        sortableColumns={ADDRESS_SELECTION_GUIDE_SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortField ? sortType : undefined}
        onSortChange={onSortChange}
      />
      <Pagination
        count={Number(count) || 25}
        page={(Number(page) || 0) + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
      <AddressSelectionGuideModal
        reFetch={getPlacesHandler}
        geoPlaces={geoPlaces}
      />
    </Wrapper.Content>
  );
};

export default AddressSelectionGuide;
